import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import heic2any from "heic2any";
import exifr from "exifr";
import { Camera, ImagePlus, Loader2, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { validateFile, FileValidationResult } from "@/lib/fileValidator";
import {
  compressImage,
  needsCompression,
  CompressionResult,
} from "@/lib/compressionService";

// ========================= ImageUploader（开始） =========================
// 该组件用于在浏览器端对 iPhone/HEIC 图片进行预处理：
// - 文件类型校验（只接受 jpg, png, heic/heif）
// - 识别 HEIC/HEIF 并转码为 WebP（优先）或 JPEG（回退）
// - 使用 browser-image-compression 压缩图片（目标：1024x1024, 500KB）
// - 读取 EXIF，用于方向、拍摄时间、GPS 等诊断
// - 通过 Canvas 统一方向并限制长边像素
// - 生成可预览的 URL 与可上传的 Blob，并返回诊断信息

export type GpsInfo = { lat: number; lng: number } | null;

export type ImageDiagnostics = {
  sourceMime: string;
  convertedMime?: string;
  width: number;
  height: number;
  orientation?: number | string;
  colorSpace: "srgb" | "unknown";
  exifDate?: string;
  gps?: GpsInfo;
  fileSizeMB: number;
  isHeic: boolean;
  isLivePhotoMov: boolean;
  warnings: string[];
};

export type CompressionStats = {
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  wasCompressed: boolean;
};

type Props = {
  maxFileSizeMB?: number;
  targetSizeMB?: number;
  targetWidthOrHeight?: number;
  maxLongEdge?: number;
  accept?: string;
  capture?: string;
  onReady: (payload: {
    previewUrl: string;
    uploadBlob: Blob;
    suggestedFileName: string;
    diagnostics: ImageDiagnostics;
    compressionStats?: CompressionStats;
  }) => void;
  onValidationError?: (result: FileValidationResult) => void;
  className?: string;
  children?: React.ReactNode;
};

function getFileExtensionByMime(mime: string): string {
  if (mime.includes("webp")) return "webp";
  if (mime.includes("jpeg")) return "jpg";
  if (mime.includes("png")) return "png";
  return "bin";
}

async function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  const img = document.createElement("img");
  const objUrl = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    img.onload = () => {
      URL.revokeObjectURL(objUrl);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(objUrl);
      reject(e);
    };
    img.src = objUrl;
  });
}

async function convertHeicIfNeeded(file: File): Promise<Blob> {
  const isHeic =
    /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);
  if (!isHeic) return file;
  try {
    const webpBlob = (await heic2any({
      blob: file,
      toType: "image/webp",
      quality: 0.9,
    })) as Blob;
    return webpBlob;
  } catch {
    try {
      const jpgBlob = (await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.92,
      })) as Blob;
      return jpgBlob;
    } catch {
      return file;
    }
  }
}

async function readDiagnostics(
  fileOrBlob: Blob,
  originFile: File,
  maxFileSizeMB: number,
): Promise<ImageDiagnostics> {
  const isHeic =
    /heic|heif/i.test(originFile.type) ||
    /\.(heic|heif)$/i.test(originFile.name);
  const isLivePhotoMov =
    /\.mov$/i.test(originFile.name) || /quicktime/i.test(originFile.type);

  let orientation: number | string | undefined;
  let exifDate: string | undefined;
  let gps: GpsInfo = null;
  try {
    const exif = await exifr.parse(fileOrBlob, {
      tiff: true,
      ifd1: true,
      exif: true,
      gps: true,
    });
    orientation = (exif as any)?.Orientation;
    const date = (exif as any)?.DateTimeOriginal as Date | undefined;
    if (date) exifDate = new Date(date).toISOString();
    const lat = (exif as any)?.latitude as number | undefined;
    const lng = (exif as any)?.longitude as number | undefined;
    if (typeof lat === "number" && typeof lng === "number") gps = { lat, lng };
  } catch {
    // ignore
  }

  const img = await loadImageFromBlob(fileOrBlob);
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  const fileSizeMB = Number((fileOrBlob.size / (1024 * 1024)).toFixed(2));

  const warnings: string[] = [];
  if (fileSizeMB > maxFileSizeMB)
    warnings.push(`文件过大：${fileSizeMB}MB > ${maxFileSizeMB}MB`);
  if (Math.max(width, height) < 1024)
    warnings.push("分辨率过低，建议长边 ≥ 1024");
  if (Math.max(width, height) / Math.min(width, height) > 3)
    warnings.push("长宽比异常，建议 ≤ 3:1");

  return {
    sourceMime: originFile.type || "unknown",
    width,
    height,
    orientation,
    colorSpace: "srgb",
    exifDate,
    gps,
    fileSizeMB,
    isHeic,
    isLivePhotoMov,
    warnings,
  };
}

function getTargetSize(
  naturalWidth: number,
  naturalHeight: number,
  maxLongEdge: number,
) {
  const scale = Math.min(
    1,
    maxLongEdge / Math.max(naturalWidth, naturalHeight),
  );
  return {
    width: Math.round(naturalWidth * scale),
    height: Math.round(naturalHeight * scale),
  };
}

async function normalizeToCanvas(
  img: HTMLImageElement,
  orientation?: number | string,
  maxLongEdge = 2500,
) {
  const naturalW = img.naturalWidth;
  const naturalH = img.naturalHeight;
  const needRotate90 = Number(orientation) === 6 || Number(orientation) === 8;
  const size = getTargetSize(
    needRotate90 ? naturalH : naturalW,
    needRotate90 ? naturalW : naturalH,
    maxLongEdge,
  );

  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) throw new Error("Canvas context unavailable");
  ctx.imageSmoothingQuality = "high";
  ctx.save();
  if (Number(orientation) === 6) {
    ctx.translate(size.width, 0);
    ctx.rotate(Math.PI / 2);
  } else if (Number(orientation) === 8) {
    ctx.translate(0, size.height);
    ctx.rotate(-Math.PI / 2);
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  ctx.restore();
  return canvas;
}

export default function ImageUploader({
  maxFileSizeMB = 15,
  targetSizeMB = 0.5,
  targetWidthOrHeight = 2048,
  maxLongEdge = 2500,
  accept = ".jpg,.jpeg,.png,.heic,.heif,.webp",
  capture,
  onReady,
  onValidationError,
  className,
  children,
}: Props) {
  const { t } = useTranslation("common");
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [isChildHovered, setIsChildHovered] = useState<boolean>(false);
  const activeHover = isHovering && !isChildHovered;

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processingStatus, setProcessingStatus] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");
  const inputAccept = useMemo(() => accept, [accept]);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || !files.length) return;
      const origin = files[0];

      setValidationError("");
      // 清除旧的预览，显示处理状态
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl("");
      }

      const validation = validateFile(origin, { maxFileSizeMB });
      if (!validation.valid) {
        setValidationError(
          validation.error || t("imageUploader.validationFailed"),
        );
        onValidationError?.(validation);
        return;
      }

      if (/\.mov$/i.test(origin.name) || /quicktime/i.test(origin.type)) {
        const error = t("imageUploader.livePhotoError");
        setValidationError(error);
        onValidationError?.({ valid: false, error });
        return;
      }

      setIsProcessing(true);
      setProcessingStatus(t("imageUploader.processing"));

      try {
        const originalSize = origin.size;

        setProcessingStatus(t("imageUploader.converting"));
        const converted = await convertHeicIfNeeded(origin);

        let compressedBlob: Blob = converted;
        let compressionResult: CompressionResult | null = null;

        const convertedFile = new File(
          [converted],
          origin.name.replace(/\.(heic|heif)$/i, ".jpg"),
          { type: converted.type || "image/jpeg" },
        );

        const shouldCompress = await needsCompression(
          convertedFile,
          targetSizeMB,
          targetWidthOrHeight,
        );

        if (shouldCompress) {
          setProcessingStatus(t("imageUploader.opening"));
          try {
            compressionResult = await compressImage(convertedFile, {
              maxSizeMB: targetSizeMB,
              maxWidthOrHeight: targetWidthOrHeight,
              onProgress: (progress) => {
                setProcessingStatus(
                  t("imageUploader.openingProgress", {
                    progress: Math.round(progress),
                  }),
                );
              },
            });
            compressedBlob = compressionResult.compressedFile;

            // 输出压缩详情到控制台
            console.log("📸 图片压缩完成:", {
              原始大小: `${(originalSize / 1024).toFixed(1)} KB`,
              压缩后大小: `${(compressionResult.compressedSize / 1024).toFixed(1)} KB`,
              压缩比: `${Math.round(compressionResult.compressionRatio * 100)}%`,
              压缩后尺寸: `${compressionResult.width} x ${compressionResult.height}`,
              文件类型: compressedBlob.type,
            });
          } catch (err) {
            console.warn("Compression failed, using converted file:", err);
          }
        } else {
          console.log("📸 图片无需压缩:", {
            文件大小: `${(convertedFile.size / 1024).toFixed(1)} KB`,
            目标上限: `${targetSizeMB * 1024} KB`,
          });
        }

        setProcessingStatus(t("imageUploader.analyzing"));
        const diagnostics = await readDiagnostics(
          compressedBlob,
          origin,
          maxFileSizeMB,
        );

        setProcessingStatus(t("imageUploader.finalizing"));
        const img = await loadImageFromBlob(compressedBlob);
        const canvas = await normalizeToCanvas(
          img,
          diagnostics.orientation,
          maxLongEdge,
        );

        const mime = compressedBlob.type || "image/jpeg";
        const uploadBlob = await new Promise<Blob>((resolve) =>
          canvas.toBlob((b) => resolve(b as Blob), mime, 0.9),
        );
        const preview = URL.createObjectURL(uploadBlob);
        setPreviewUrl(preview);

        const suggestedExt = getFileExtensionByMime(mime);
        const baseName = origin.name.replace(/\.[^.]+$/, "");
        const suggestedFileName = `${baseName}.${suggestedExt}`;

        const stats: CompressionStats = {
          originalSize,
          compressedSize: uploadBlob.size,
          compressionRatio:
            originalSize > 0 ? 1 - uploadBlob.size / originalSize : 0,
          wasCompressed: shouldCompress,
        };

        onReady({
          previewUrl: preview,
          uploadBlob,
          suggestedFileName,
          diagnostics: {
            ...diagnostics,
            convertedMime: mime,
            width: canvas.width,
            height: canvas.height,
            fileSizeMB: Number((uploadBlob.size / (1024 * 1024)).toFixed(2)),
          },
          compressionStats: stats,
        });
      } catch (err) {
        console.error("Image processing failed:", err);
        setValidationError(t("imageUploader.processingFailed"));
      } finally {
        setIsProcessing(false);
        setProcessingStatus("");
      }
    },
    [
      maxFileSizeMB,
      targetSizeMB,
      targetWidthOrHeight,
      maxLongEdge,
      previewUrl,
      onReady,
      onValidationError,
    ],
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFiles(files);
      }
    },
    [handleFiles],
  );

  return (
    <div
      className={`relative ${className}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <motion.label
        className={`relative flex flex-col items-center justify-center w-full h-full cursor-pointer overflow-hidden ${
          isDragging
            ? "bg-primary/5 dark:bg-primary/10"
            : "bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-zinc-900 dark:to-zinc-800/80"
        }`}
        onHoverStart={() => setIsHovering(true)}
        onHoverEnd={() => setIsHovering(false)}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.3 }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={t("imageUploader.preview")}
            className="w-full h-full object-contain"
          />
        ) : isProcessing ? (
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <Loader2 className="w-10 h-10 text-primary animate-spin mb-3" />
            <p className="text-sm text-slate-600 dark:text-zinc-300">
              {processingStatus}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <AnimatePresence>
              {validationError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-4 left-4 right-4 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2"
                >
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {validationError}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div
              className="relative mb-4"
              animate={{
                scale: isDragging ? 1.15 : activeHover ? 1.08 : 1,
                y: activeHover ? -4 : 0,
              }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
            >
              <motion.div
                className="absolute inset-0 rounded-full bg-primary/10 dark:bg-primary/20 blur-xl"
                animate={{
                  scale: isDragging ? 2 : activeHover ? 1.8 : 1.5,
                  opacity: isDragging ? 0.8 : activeHover ? 0.6 : 0.3,
                }}
                transition={{ duration: 0.3 }}
              />
              <motion.div
                className={`relative w-16 h-16 rounded-full flex items-center justify-center ${
                  isDragging
                    ? "bg-primary/20 dark:bg-primary/30 border-2 border-primary border-dashed"
                    : "bg-white dark:bg-zinc-700/90 shadow-lg shadow-slate-200/50 dark:shadow-black/30"
                }`}
                animate={{
                  boxShadow:
                    activeHover && !isDragging
                      ? "0 8px 30px rgba(0,0,0,0.12)"
                      : "0 4px 15px rgba(0,0,0,0.08)",
                }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  animate={{
                    rotate: activeHover && !isDragging ? [0, -10, 10, 0] : 0,
                  }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                  {isDragging ? (
                    <ImagePlus className="w-7 h-7 text-primary" />
                  ) : (
                    <Camera
                      className={`w-7 h-7 transition-colors duration-300 ${
                        activeHover
                          ? "text-primary"
                          : "text-slate-400 dark:text-zinc-400"
                      }`}
                    />
                  )}
                </motion.div>
              </motion.div>
            </motion.div>

            <motion.p
              className={`text-sm font-medium mb-1 transition-colors duration-300 ${
                isDragging || activeHover
                  ? "text-primary"
                  : "text-slate-600 dark:text-zinc-300"
              }`}
              animate={{ y: activeHover ? -2 : 0 }}
              transition={{ duration: 0.2 }}
            >
              {isDragging
                ? t("imageUploader.dropToUpload")
                : t("imageUploader.clickOrDrag")}
            </motion.p>

            <motion.p
              className="text-xs text-slate-400 dark:text-zinc-500"
              animate={{
                opacity: activeHover ? 0.8 : 1,
                y: activeHover ? -1 : 0,
              }}
              transition={{ duration: 0.2 }}
            >
              {t("imageUploader.supportedFormats")}
            </motion.p>
          </div>
        )}

        {/* 压缩统计信息已移除，用户无需了解技术细节 */}

        <input
          type="file"
          accept={inputAccept}
          capture={capture as any}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />

        <motion.div
          className="absolute inset-2 rounded-lg border-2 border-dashed pointer-events-none"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{
            opacity: isDragging ? 1 : 0,
            scale: isDragging ? 1 : 0.95,
            borderColor: isDragging
              ? "var(--primary)"
              : "oklch(from var(--primary) 1 0 0.6)",
          }}
          transition={{ duration: 0.2 }}
        />
      </motion.label>

      {/* 底部额外内容 - 放在 label 外部，避免干扰文件选择 */}
      {children && !previewUrl && !isProcessing && (
        <div
          className="absolute bottom-3 left-0 right-0 z-20 flex justify-center"
          onMouseEnter={() => setIsChildHovered(true)}
          onMouseLeave={() => setIsChildHovered(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}
// ========================= ImageUploader（结束） =========================
